import { State, Action, StateContext, Selector } from '@ngxs/store';
import { Injectable } from '@angular/core';

export interface QuestionResponse {
  questionKey: string;
  questionId: string | number;
  questionText: string;
  answerId: string | number;
  answerText: string;
  reasons: string[];
  imageUrl?: string;
  order: number;
}

export interface ChecklistStateModel {
  idTrade: string;
  imeiBiped: string;
  selectedTrade: any;
  selectedTrades: any[];
  identification: { [key: string]: string };
  identificationStepItems: any[];
  questions: QuestionResponse[];
}

export class LoadDeviceQuestions {
  static readonly type = '[Checklist] Load Device Questions';
  constructor(public questions: any[]) {}
}

export class UpdateQuestionResponse {
  static readonly type = '[Checklist] Update Question Response';
  constructor(
    public questionKey: string,
    public payload: Partial<QuestionResponse>
  ) {}
}

export class UploadEvidenceImage {
  static readonly type = '[Checklist] Upload Evidence Image';
  constructor(
    public questionKey: string,
    public imageUrl: string
  ) {}
}

export class UpdateIdentificationField {
  static readonly type = '[Checklist] Update Identification Field';
  constructor(
    public field: string,
    public value: string
  ) {}
}

export class SetIdentificationStepItems {
  static readonly type = '[Checklist] Set Identification Step Items';
  constructor(public stepItems: any[]) {}
}

export class SetImeiBiped {
  static readonly type = '[Checklist] Set Imei Biped';
  constructor(public imei: string) {}
}

export class SetSelectedTrade {
  static readonly type = '[Checklist] Set Selected Trade';
  constructor(public trade: any) {}
}

export class SetSelectedTrades {
  static readonly type = '[Checklist] Set Selected Trades';
  constructor(public trades: any[]) {}
}

export class ClearChecklist {
  static readonly type = '[Checklist] Clear Checklist';
}

@State<ChecklistStateModel>({
  name: 'checklist',
  defaults: {
    idTrade: '',
    imeiBiped: '',
    selectedTrade: null,
    selectedTrades: [],
    identification: {},
    identificationStepItems: [],
    questions: [],
  },
})
@Injectable()
export class ChecklistState {
  @Selector()
  static getFullChecklist(state: ChecklistStateModel) {
    return {
      idTrade: state.idTrade,
      identification: state.identification,
      identificationStepItems: state.identificationStepItems,
      questions: state.questions,
    };
  }

  @Selector()
  static getSelectedTrade(state: ChecklistStateModel) {
    return state.selectedTrade;
  }

  @Selector()
  static getSelectedTrades(state: ChecklistStateModel) {
    return state.selectedTrades || [];
  }

  @Selector()
  static getImeiBiped(state: ChecklistStateModel) {
    return state.imeiBiped;
  }

  @Selector()
  static getQuestions(state: ChecklistStateModel) {
    return state.questions;
  }

  @Action(LoadDeviceQuestions)
  loadQuestions(ctx: StateContext<ChecklistStateModel>, { questions }: LoadDeviceQuestions) {
    const mapped = questions.map((q: any) => ({
      questionKey: q.key,
      questionId: q.key,
      questionText: q.label,
      answerId: '',
      answerText: '',
      reasons: [],
      imageUrl: undefined,
      order: q.order,
    }));

    ctx.patchState({ questions: mapped });
  }

  @Action(UpdateQuestionResponse)
  updateResponse(
    ctx: StateContext<ChecklistStateModel>,
    { questionKey, payload }: UpdateQuestionResponse
  ) {
    const state = ctx.getState();
    const questions = state.questions.map((q) =>
      q.questionKey === questionKey ? { ...q, ...payload } : q
    );
    ctx.patchState({ questions });
  }

  @Action(UploadEvidenceImage)
  uploadImage(
    ctx: StateContext<ChecklistStateModel>,
    { questionKey, imageUrl }: UploadEvidenceImage
  ) {
    const state = ctx.getState();
    const questions = state.questions.map((q) =>
      q.questionKey === questionKey ? { ...q, imageUrl } : q
    );
    ctx.patchState({ questions });
  }

  @Action(UpdateIdentificationField)
  updateIdentification(
    ctx: StateContext<ChecklistStateModel>,
    { field, value }: UpdateIdentificationField
  ) {
    const state = ctx.getState();
    ctx.patchState({
      identification: { ...state.identification, [field]: value },
    });
  }

  @Action(SetIdentificationStepItems)
  setStepItems(ctx: StateContext<ChecklistStateModel>, { stepItems }: SetIdentificationStepItems) {
    ctx.patchState({ identificationStepItems: stepItems });
  }

  @Action(SetImeiBiped)
  setImeiBiped(ctx: StateContext<ChecklistStateModel>, { imei }: SetImeiBiped) {
    ctx.patchState({ imeiBiped: imei });
  }

  @Action(SetSelectedTrade)
  setSelectedTrade(ctx: StateContext<ChecklistStateModel>, { trade }: SetSelectedTrade) {
    ctx.patchState({ selectedTrade: trade });
  }

  @Action(SetSelectedTrades)
  setSelectedTrades(ctx: StateContext<ChecklistStateModel>, { trades }: SetSelectedTrades) {
    ctx.patchState({ selectedTrades: trades });
  }

  @Action(ClearChecklist)
  clearChecklist(ctx: StateContext<ChecklistStateModel>) {
    ctx.patchState({
      idTrade: '',
      imeiBiped: '',
      selectedTrade: null,
      selectedTrades: [],
      identification: {},
      identificationStepItems: [],
      questions: [],
    });
  }
}
